import { useMemo, useEffect, useState } from 'react';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import type { NetworksApiResponse, SelectedNetwork } from '@/types';

interface UseLaunchDarklyContextParams {
  selectedNetwork: SelectedNetwork;
  userId?: string;
  isImpersonating?: boolean;
  allNetworks?: NetworksApiResponse;
}

/**
 * Single owner of `ldClient.identify()`. Sets a multi-context with the user
 * (including all their network IDs) and the impersonation state as soon as
 * those are known, adding the `network` context only once a network has been
 * selected.
 */
const useLaunchDarklyContext = ({
  selectedNetwork,
  userId,
  isImpersonating,
  allNetworks,
}: UseLaunchDarklyContextParams) => {
  const ldClient = useLDClient();
  const [isContextReady, setIsContextReady] = useState(false);

  const allNetworkIds = useMemo(() => {
    if (!allNetworks) return undefined;
    return [
      ...new Set((allNetworks.revenue ?? []).concat(allNetworks.insights ?? []).map((n) => n.id)),
    ];
  }, [allNetworks]);

  useEffect(() => {
    let isCancelled = false;

    const identifyContext = async () => {
      if (!userId || !allNetworkIds) return;

      if (!ldClient) {
        if (!isCancelled) {
          setIsContextReady(true);
        }
        return;
      }


      const networkContext = selectedNetwork?.id
        ? { network: { kind: 'network' as const, key: selectedNetwork.id } }
        : {};

      const contexts = {
        kind: 'multi' as const,
        user: {
          kind: 'user' as const,
          key: userId,
          networks: allNetworkIds,
        },
        impersonation: {
          kind: 'impersonation' as const,
          key: isImpersonating ? 'active' : 'inactive',
        },
        ...networkContext,
      };

      try {
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('LD context timeout')), 1500);
        });

        await Promise.race([ldClient.identify(contexts), timeoutPromise]);
      } catch (error) {
        if (error instanceof Error && error.message === 'LD context timeout') {
          console.warn('LaunchDarkly identify failed or timed out:', error);
        }
      }

      if (!isCancelled) {
        setIsContextReady(true);
      }
    };

    identifyContext();

    return () => {
      isCancelled = true;
    };
  }, [ldClient, selectedNetwork?.id, userId, isImpersonating, allNetworkIds]);

  return { isContextReady };
};

export default useLaunchDarklyContext;
